import type { CourseLesson, CourseModule } from './api';
import { lockedLessonIds } from './drip';

/** Where the course page's "Weiterlernen" row sends a returning learner. */
export interface CourseResume {
  moduleId: string;
  moduleTitle: string;
  lesson: CourseLesson;
}

/** One module as the course page's module list needs it. */
export interface ModuleSummary {
  module: CourseModule;
  totalLessons: number;
  completedLessons: number;
  /** 0–100, rounded. */
  pct: number;
  /**
   * Every lesson done. Counted, never read off `pct` — that's rounded, so
   * 199/200 would round to 100 and claim a module was finished.
   */
  completed: boolean;
  /**
   * Locked exactly when every lesson inside is locked by the per-lesson drip
   * walk — a derived reading of the existing rule, not a second lock model.
   */
  locked: boolean;
}

export interface CourseView {
  /** Non-empty modules in display order, each with its own counts and lock. */
  modules: ModuleSummary[];
  /** The module this course collapses into its course page, or null. */
  soleModule: CourseModule | null;
  totalLessons: number;
  completedLessons: number;
  /** 0–100, rounded; 0 for a course with no lessons. */
  pct: number;
  /**
   * Drip-locked lesson ids for the whole course. The single-module case renders
   * lessons directly and needs them; the module rows have `locked` instead.
   */
  lockedLessonIds: ReadonlySet<string>;
  /** The lesson to continue with, or null when the course is finished. */
  resume: CourseResume | null;
}

/**
 * Everything the course page reads off a `/api/courses/:slug` payload: the
 * module list, the course aggregate, and the drip locks.
 *
 * **Empty modules are dropped.** A published module with zero published lessons
 * is representable, and would otherwise be a tappable dead end reading
 * "0 Lektionen". It disappears from the list *and* from the aggregate — the
 * aggregate is summed from the modules that survive, so the two can't drift.
 *
 * The drip walk runs over the whole module list, not per module: a lesson is
 * locked by every incomplete lesson before it in the course, including ones in
 * earlier modules.
 */
/**
 * The single module a course collapses into its course page — or null when the
 * course has a real module level.
 *
 * A course with exactly one module renders that module's lessons on the course
 * page itself and never links to the module page. For those courses the module
 * level is not a surface the learner has ever seen, so nothing may send them
 * there: the free Masterclass is this case, and it is the funnel conversion
 * path, so it must not gain a tap. Every page that would otherwise point at a
 * module asks here first.
 *
 * Empty modules don't count — the course page drops them, so one real module
 * beside a placeholder still collapses.
 */
export function soleModule(modules: CourseModule[]): CourseModule | null {
  const withLessons = modules.filter((m) => m.lessons.length > 0);
  return withLessons.length === 1 ? withLessons[0]! : null;
}

/**
 * The lesson a returning learner should continue with: the **first incomplete
 * lesson in display order** — modules in order, lessons within each — or null
 * when there is nothing left to watch.
 *
 * Display order, not recency, is the rule: the course-detail payload carries no
 * "last touched" timestamp per lesson, so true recency isn't computable here.
 * The upside is that it also picks up a lesson the learner skipped past rather
 * than stranding it.
 *
 * A drip course needs no extra care: the drip walk locks everything *after* the
 * first incomplete lesson, so the lesson this returns is by construction the one
 * open lesson.
 *
 * Empty modules are skipped, matching the module list — an empty module holds
 * nothing to continue with anyway.
 */
export function courseResume(modules: CourseModule[]): CourseResume | null {
  for (const m of modules) {
    for (const l of m.lessons) {
      if (!l.progress.completed) {
        return { moduleId: m.id, moduleTitle: m.title, lesson: l };
      }
    }
  }
  return null;
}

export function courseView(modules: CourseModule[], sequentialUnlock: boolean): CourseView {
  const lockedIds = lockedLessonIds(modules, sequentialUnlock);

  const summaries = modules
    .filter((m) => m.lessons.length > 0)
    .map((m) => {
      const completedLessons = m.lessons.filter((l) => l.progress.completed).length;
      return {
        module: m,
        totalLessons: m.lessons.length,
        completedLessons,
        pct: Math.round((completedLessons / m.lessons.length) * 100),
        completed: completedLessons === m.lessons.length,
        // Safe only because empty modules are already gone: every() over no
        // lessons is vacuously true, which would lock an empty module.
        locked: m.lessons.every((l) => lockedIds.has(l.id)),
      };
    });

  const totalLessons = summaries.reduce((n, s) => n + s.totalLessons, 0);
  const completedLessons = summaries.reduce((n, s) => n + s.completedLessons, 0);

  return {
    modules: summaries,
    soleModule: soleModule(modules),
    totalLessons,
    completedLessons,
    pct: totalLessons === 0 ? 0 : Math.round((completedLessons / totalLessons) * 100),
    lockedLessonIds: lockedIds,
    // Over the surviving modules, so an empty module can't be pointed at.
    resume: courseResume(summaries.map((s) => s.module)),
  };
}
